/**
 * Aspect — Walk-mode distance culling ("occlusion culling lite")
 * --------------------------------------------------------------
 * Interior scenes hide most of the home behind walls: from inside a room you
 * can only ever see that room plus slivers of neighbours through doorways.
 * So in walk mode we disable furniture entities that are BOTH outside the
 * player's current room AND beyond a show radius — the renderer then skips
 * their draw calls, shadows, and skinning entirely.
 *
 * Rules:
 *  - Items in the room the player stands in are ALWAYS visible (long hallway
 *    sight lines never pop).
 *  - Everything else uses a radius with hysteresis (show at 11 m, hide past
 *    13 m) so pieces straddling the threshold don't flicker.
 *  - Overhead / plan-editor views show the whole home (culling suspended and
 *    every entity re-enabled).
 *
 * Entities are toggled via `entity.enabled` only — the ObjectPool's
 * active/free bookkeeping is never touched, so spawn/release stay correct.
 */

const TICK_S = 0.28; // re-evaluate ~3.5×/s — cheap (one distance per entity)

export class DistanceCulling {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {import('playcanvas').Entity} camera
   */
  constructor(app, scene, camera, opts = {}) {
    this.app = app;
    this.scene = scene;
    this.camera = camera;
    this.showRadius = opts.showRadius ?? 11;
    this.hideRadius = opts.hideRadius ?? 13;
    this.roomMargin = opts.roomMargin ?? 0.9;

    /** Master switch (dev/debug). */
    this.enabled = true;
    /** Culling only runs in first-person walk mode. */
    this._walkMode = true;
    this._planOpen = false;
    this._acc = 0;
    /** @type {Set<string>} placement ids currently hidden by the culler */
    this._hidden = new Set();

    app.on("update", (dt) => this._tick(dt));
    app.on("cam:mode-changed", ({ mode }) => {
      this._walkMode = mode !== "overhead";
      if (!this._active()) this._showAll();
    });
    app.on("plan-editor:opened", () => {
      this._planOpen = true;
      this._showAll();
    });
    app.on("plan-editor:closed", () => {
      this._planOpen = false;
    });
    // Fresh room: pooled entities were released; stale ids must not linger.
    app.on("scene:room-loaded", () => this._hidden.clear());
  }

  _active() {
    return this.enabled && this._walkMode && !this._planOpen;
  }

  _tick(dt) {
    if (!this._active()) return;
    this._acc += dt ?? 0.016;
    if (this._acc < TICK_S) return;
    this._acc = 0;

    const cam = this.camera?.getPosition?.();
    if (!cam) return;
    const rect = this.scene.roomAt?.(cam.x, cam.z) ?? null;
    const M = this.roomMargin;
    const showR2 = this.showRadius * this.showRadius;
    const hideR2 = this.hideRadius * this.hideRadius;
    let changed = 0;

    for (const [id, entity] of this.scene.entities) {
      if (!entity) continue;
      const pos = this.scene.placements.get(id)?.position;
      if (!pos) continue;

      const inRoom =
        rect &&
        pos[0] >= rect.x0 - M &&
        pos[0] <= rect.x1 + M &&
        pos[2] >= rect.z0 - M &&
        pos[2] <= rect.z1 + M;

      let visible = true;
      if (!inRoom) {
        const d2 = (pos[0] - cam.x) ** 2 + (pos[2] - cam.z) ** 2;
        // Hysteresis: hidden items need to come closer (showR) than the
        // distance at which visible items get dropped (hideR).
        visible = this._hidden.has(id) ? d2 <= showR2 : d2 <= hideR2;
      }

      if (visible) {
        if (this._hidden.delete(id) || !entity.enabled) {
          entity.enabled = true;
          changed++;
        }
      } else if (!this._hidden.has(id)) {
        this._hidden.add(id);
        entity.enabled = false;
        changed++;
      }
    }

    if (changed) this.app.renderNextFrame = true;
  }

  /** Re-enable every culled entity (leaving walk mode / entering plan). */
  _showAll() {
    if (!this._hidden.size) return;
    for (const id of this._hidden) {
      const entity = this.scene.entities.get(id);
      if (entity) entity.enabled = true;
    }
    this._hidden.clear();
    this.app.renderNextFrame = true;
  }
}

export default DistanceCulling;
